import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { Progress } from 'reactstrap';
import dayjs from 'dayjs';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { useLocalizedImage } from '../../Hooks/useLocalizedMedia';
import { useActiveChallenge } from '../../Hooks/useActiveChallenge';
import { Theme } from '../../Theme/Theme';
import { getMediaLarge, getMediaMedium } from '../../Model/Media/Media';
import { ChallengeExtraMile } from '../Motivation/Components/ChallengeExtraMile';

const Container = styled.div`
  border-radius: 20px;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #3c3c3c 0%, #2e2e2e 100%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);

    .bg-image {
      transform: scale(1.05);
    }
  }
`;

const CardInner = styled.div`
  display: flex;
  min-height: 200px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const ImageSection = styled.div`
  flex: 0 0 50%;
  position: relative;
  overflow: hidden;
  min-height: 160px;

  @media (max-width: 600px) {
    flex: none;
    height: 140px;
  }
`;

const BackgroundImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: 0;
  background: ${({ image }) =>
    image
      ? `url('${image}')`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;
  transition: transform 0.4s ease;
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 50%, rgba(60, 60, 60, 0.95) 100%);

  @media (max-width: 600px) {
    background: linear-gradient(180deg, transparent 30%, rgba(60, 60, 60, 0.95) 100%);
  }
`;

const ContentSection = styled.div`
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Badge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 14px;
  opacity: 0.9;
  margin: 0 0 20px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: 24px;
  font-weight: 700;
`;

const StatLabel = styled.span`
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgressContainer = styled.div`
  margin-top: 16px;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  margin-bottom: 8px;
  opacity: 0.9;
`;

const StyledProgress = styled(Progress)`
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;

  .progress-bar {
    background: white;
    border-radius: 4px;
  }
`;

const TimeLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  opacity: 0.9;
  margin-top: 12px;

  i {
    font-size: 14px;
  }
`;

const EmptyState = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px dashed ${Theme.Colors.lighterGrey};

  &:hover {
    border-color: ${Theme.Colors.primary};
    background: ${Theme.Colors.primary}08;
  }
`;


const EmptyTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0;
`;

export const ActiveChallengeCard: React.FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();

  const { data: activeChallenge, isLoading } = useActiveChallenge();

  const challenge = activeChallenge?.challenge;
  const challengeName = useLocalized(challenge?.name);
  const challengeDescription = useLocalized(challenge?.description);
  const challengeImage = useLocalizedImage(challenge?.images);
  const challangeStatus: 'running' | 'upcoming' | 'ended' = challenge?.ended
    ? 'ended'
    : challenge?.started
      ? 'running'
      : 'upcoming';

  const handleClick = () => {
    if (activeChallenge) {
      navigate(`/motivation/challenge/${activeChallenge.challenge.id}`);
    } else {
      navigate('/motivation');
    }
  };

  if (isLoading) {
    return null;
  }

  if (!activeChallenge) {
    return (
      <EmptyState onClick={handleClick}>
        <EmptyTitle>{t('home.challenge.joinTitle', { defaultValue: 'Join a Challenge!' })}</EmptyTitle>
        <EmptyDescription>
          {t('home.challenge.joinDescription', {
            defaultValue: 'Compete with others, track your progress, and stay motivated.',
          })}
        </EmptyDescription>
      </EmptyState>
    );
  }

  const { myProgress = 0, totalGoal = 100, rank } = activeChallenge;
  const progressPercent = totalGoal > 0 ? Math.min(100, (myProgress / totalGoal) * 100) : 0;

  const endDate = challenge?.endDateTime;
  const daysLeft = endDate ? dayjs(endDate).diff(dayjs(), 'day') : 0;
  const startDate = challenge?.startDateTime;
  const startsIn = startDate ? dayjs(startDate).fromNow() : undefined;

  return (
    <Container onClick={handleClick}>
      <CardInner>
        <ImageSection>
          <BackgroundImage className="bg-image" image={challengeImage ? getMediaLarge(challengeImage) : undefined} />
          <ImageOverlay />
        </ImageSection>
        <ContentSection>
          <Header>
            {challangeStatus === 'running'
              ? <Badge>{t('home.challenge.active', { defaultValue: 'Active Challenge' })}</Badge>
              : challangeStatus === 'upcoming'
                ? <Badge>{t('home.challenge.starting', { defaultValue: 'Starts {{days}}', days: startsIn ?? '' })}</Badge>
                : <Badge>{t('home.challenge.ended', { defaultValue: 'Challenge Ended' })}</Badge>}

          </Header>

          <Title>{challengeName}</Title>
          {challengeDescription && <Description>{challengeDescription}</Description>}

          {challangeStatus === 'running' && rank !== undefined && (
            <>
            <StatsRow>
              <Stat>
                <StatValue>{Math.round(myProgress).toLocaleString()}</StatValue>
                <StatLabel>{t('home.challenge.points', { defaultValue: 'Points' })}</StatLabel>
              </Stat>
              {rank && (
                <Stat>
                  <StatValue>#{rank}</StatValue>
                  <StatLabel>{t('home.challenge.rank', { defaultValue: 'Rank' })}</StatLabel>
                </Stat>
              )}
            </StatsRow>

            <ProgressContainer>
              <ProgressLabel>
                <span>{t('home.challenge.progress', { defaultValue: 'Progress' })}</span>
                <span>{Math.round(progressPercent)}%</span>
              </ProgressLabel>
              <StyledProgress value={progressPercent} />
            </ProgressContainer>

            {daysLeft > 0 && (
              <TimeLeft>
                <i className="bi bi-clock" />
                {t('home.challenge.daysLeft', { defaultValue: '{{days}} days left', days: daysLeft })}
              </TimeLeft>
            )}
            </>
          )}
          <ChallengeExtraMile
            challenge={activeChallenge.challenge}
            totalPoints={myProgress}
            extraPoints={activeChallenge.extraCollectedPoints}
            goalPoints={totalGoal}
            compact
          />
        </ContentSection>
      </CardInner>
    </Container>
  );
});
